import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
  FirestoreError,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QueryConstraint,
  QuerySnapshot,
  setDoc,
  SetOptions,
  Unsubscribe,
  updateDoc,
  where,
  WhereFilterOp,
  runTransaction,
  Timestamp,
  serverTimestamp,
  Transaction,
  writeBatch,
  WriteBatch,
  increment,
  arrayUnion,
  FieldValue,
} from 'firebase/firestore';

export class CrewdleFirestore {
  private firestore: Firestore;
  private ngZone: { run: <T>(fn: () => T) => T };

  constructor(firestore: Firestore, ngZone?: { run: <T>(fn: () => T) => T }) {
    this.firestore = firestore;
    this.ngZone = ngZone || { run: (fn) => fn() };
  }

  public doc(path: string, ...pathSegments: string[]): DocumentReference<DocumentData> {
    return doc(this.firestore, path, ...pathSegments);
  }

  public docListen(
    path: string,
    onNext: (snapshot: DocumentSnapshot<DocumentData>) => void,
    onError?: (error: FirestoreError) => void,
    onCompletion?: () => void
  ): Unsubscribe {
    const callerStack = new Error().stack;
    const wrappedOnNext = (snapshot: DocumentSnapshot<DocumentData>) => {
      this.ngZone.run(() => onNext(snapshot));
    };
    const wrappedOnError = (error: FirestoreError) => {
      console.error(`[Firestore] docListen error on "${path}":`, error.code, error.message, '\nListener created at:', callerStack);
      this.ngZone.run(() => onError?.(error));
    };
    return onSnapshot(doc(this.firestore, path), wrappedOnNext, wrappedOnError, onCompletion);
  }

  public docGet(path: string): Promise<DocumentSnapshot<DocumentData>> {
    return getDoc(doc(this.firestore, path)).catch(error => {
      console.error(`[Firestore] docGet error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public docAdd(path: string, data: object): Promise<DocumentReference<object>> {
    return addDoc(collection(this.firestore, path), data).catch(error => {
      console.error(`[Firestore] docAdd error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public docSet(path: string, data: object, options?: SetOptions): Promise<void> {
    return setDoc(doc(this.firestore, path), data, options).catch(error => {
      console.error(`[Firestore] docSet error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public docUpdate(path: string, data: object): Promise<void> {
    return updateDoc(doc(this.firestore, path), data).catch(error => {
      console.error(`[Firestore] docUpdate error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public docDelete(path: string): Promise<void> {
    return deleteDoc(doc(this.firestore, path)).catch(error => {
      console.error(`[Firestore] docDelete error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public collectionGet(path: string, ...queryConstraints: QueryConstraint[]): Promise<QuerySnapshot<DocumentData>> {
    return getDocs(query(collection(this.firestore, path), ...queryConstraints)).catch(error => {
      console.error(`[Firestore] collectionGet error on "${path}":`, error.code, error.message);
      throw error;
    });
  }

  public collectionListen(
    path: string,
    queryConstraints: QueryConstraint[],
    onNext: (snapshot: QuerySnapshot<DocumentData>) => void,
    onError?: (error: FirestoreError) => void,
    onCompletion?: () => void
  ): Unsubscribe {
    const callerStack = new Error().stack;
    const wrappedOnNext = (snapshot: QuerySnapshot<DocumentData>) => {
      this.ngZone.run(() => onNext(snapshot));
    };
    const wrappedOnError = (error: FirestoreError) => {
      console.error(`[Firestore] collectionListen error on "${path}":`, error.code, error.message, '\nListener created at:', callerStack);
      this.ngZone.run(() => onError?.(error));
    };
    return onSnapshot(query(collection(this.firestore, path), ...queryConstraints), wrappedOnNext, wrappedOnError, onCompletion);
  }

  public deleteField() {
    return deleteField();
  }

  public increment(n: number) {
    return increment(n);
  }

  public arrayUnion(...elements: unknown[]) {
    return arrayUnion(...elements);
  }

  public where(fieldPath: string, opStr: WhereFilterOp, value: unknown): QueryConstraint {
    return where(fieldPath, opStr, value);
  }

  public collection(path: string, ...pathSegments: string[]): CollectionReference<DocumentData> {
    return collection(this.firestore, path, ...pathSegments);
  }

  public runTransaction<T>(updateFunction: (transaction: Transaction) => Promise<T>): Promise<T> {
    return runTransaction(this.firestore, updateFunction);
  }

  public async batchUpdate(updates: { path: string; data: object }[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    for (const { path, data } of updates) {
      batch.update(doc(this.firestore, path), data);
    }
    return batch.commit().catch(error => {
      console.error(`[Firestore] batchUpdate error:`, error.code, error.message);
      throw error;
    });
  }

  public async batchWrite(operations: { type: 'set' | 'update'; path: string; data: object }[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    for (const op of operations) {
      const ref = doc(this.firestore, op.path);
      if (op.type === 'set') {
        batch.set(ref, op.data);
      } else {
        batch.update(ref, op.data);
      }
    }
    return batch.commit().catch(error => {
      console.error(`[Firestore] batchWrite error:`, error.code, error.message);
      throw error;
    });
  }

  public async getCurrentTimestamp(): Promise<Date|null> {
    const ref = await this.docAdd('timestamps', {
      now: serverTimestamp(),
    });

    const timestamp = await this.listenUntilCurrentTimestampIsAvailable(ref.id);

    return this.timestampToDate(timestamp);
  }

  public timestampToDate(timestamp: Timestamp): Date|null {
    if (!timestamp) {
      return null;
    }

    return timestamp.toDate();
  }

  public dateToTimestamp(date: Date): Timestamp {
    return Timestamp.fromDate(date);
  }

  private async listenUntilCurrentTimestampIsAvailable(id: string): Promise<Timestamp> {
    const promise = new Promise<Timestamp>((resolve) => {
      const unsubscribe = this.docListen(`timestamps/${id}`, (snapshot) => {
        const timestamp = snapshot.data()?.now;
        if (!timestamp || snapshot.metadata.hasPendingWrites) {
          // Still waiting for a timestamp'
          return;
        }

        unsubscribe();
        resolve(timestamp);
      });
    });

    return promise;
  }
}
